import { ChevronDown, Eye, EyeOff } from 'lucide-react' // Using lucide-react icons
import { useState, useContext } from 'react'

import { AuthContext } from '../../contexts/auth-context'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Textarea } from '../ui/textarea'
import { Combobox } from '../ui/Combobox'
import { cn } from '../../lib/utils'

function FormControls({ formControls = [], formData, setFormData, formErrors, setFormErrors }) {
  const [showPassword, setShowPassword] = useState({})

  const { navigateCMSpage } = useContext(AuthContext)

  function togglePassword(name) {
    setShowPassword((prev) => ({
      ...prev,
      [name]: !prev[name],
    }))
  }

  // Define validation rules
  const validateField = (name, value, validations, currentFormData) => {
    if (!validations) return ''

    // Loop through validations array and check each rule
    for (const validation of validations) {
      if (validation.required && (!value || value === '')) {
        return validation.message || `${name} is required`
      }

      if (validation.pattern && value) {
        const pattern = new RegExp(validation.pattern)
        if (!pattern.test(value)) {
          return validation.message || `Invalid ${name} format`
        }
      }

      if (validation.minLength && value && value.length < validation.minLength) {
        return validation.message || `${name} should be at least ${validation.minLength} characters`
      }

      if (validation.maxLength && value && value.length > validation.maxLength) {
        return validation.message || `${name} should not exceed ${validation.maxLength} characters`
      }

      if (validation.min && value && Number(value) < validation.min) {
        return validation.message || `${name} should be at least ${validation.min}`
      }

      if (validation.max && value && Number(value) > validation.max) {
        return validation.message || `${name} should not exceed ${validation.max}`
      }

      // Custom validation function
      if (validation.custom && typeof validation.custom === 'function') {
        const customError = validation.custom(value, currentFormData)
        if (customError) return customError
      }
    }

    return ''
  }

  // Validate a specific field and update formErrors
  const validateAndUpdateField = (name, value, validations, currentFormData) => {
    const error = validateField(name, value, validations, currentFormData)
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }))
    return error
  }

  // handle form field change
  const handleChange = (name, value, validations) => {
    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData, [name]: value }

      // Trigger mobile validation when country code changes
      if (name === 'country_code') {
        const mobileControl = formControls
          .flatMap((control) => (control.isGrouped ? control.groupControls : [control]))
          .find((control) => control.name === 'mobile')

        if (mobileControl) {
          /* 
          If the mobile control exists, it:
          - Retrieves its validation rules.
          - Gets the current mobile value from the updated form data.
          - Runs validateField to check for errors.
          - Updates formErrors state with the result for the mobile field. */
          const mobileValidations = mobileControl.validations
          const mobileValue = newFormData.mobile || ''

          const error = validateField('mobile', mobileValue, mobileValidations, newFormData)
          setFormErrors((prevErrors) => ({
            ...prevErrors,
            mobile: error,
          }))
        }
      }

      return newFormData
    })

    // Validate the current field
    if (validations) {
      validateAndUpdateField(name, value, validations, formData)
    }
  }

  // handle terms and policy
  const handleTermsButtonClick = () => {
    navigateCMSpage('terms-and-conditions')
  }

  const handlePrivacyPolicyButtonClick = () => {
    navigateCMSpage('privacy-policy')
  }

  // render element based on component type
  function renderComponentByType(controlItem) {
    let element = null
    const { name, componentType, validations } = controlItem
    const value = formData[name] || ''
    const error = formErrors?.[name] || ''

    switch (componentType) {
      case 'input':
        if (controlItem.type === 'file') {
          element = (
            <div className="w-full">
              <Input
                id={name}
                name={name}
                type="file"
                accept={controlItem.accept || '*'} // Accept optional mime types
                onChange={(e) => {
                  const file = e.target.files[0]
                  handleChange(name, file, validations)
                }}
                className={`${controlItem.className || ''} ${error ? 'border-red-500' : ''}`}
              />
              {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            </div>
          )
        } else {
          element = (
            <div className="w-full">
              <div className="relative">
                <Input
                  id={name}
                  name={name}
                  placeholder={controlItem.placeholder}
                  type={
                    controlItem.type === 'password' && showPassword[controlItem.name]
                      ? 'text'
                      : controlItem.type
                  }
                  value={value}
                  onChange={(e) => handleChange(name, e.target.value, validations)}
                  onBlur={() => validateAndUpdateField(name, value, validations)}
                  className={`${controlItem.className || ''} ${
                    error ? 'border-red-500' : ''
                  } ${controlItem.type === 'password' ? 'pr-10' : ''}`}
                  onInput={(e) => {
                    // added so that user can't input spaces in start
                    e.target.value = e.target.value.replace(/^\s+/, '')
                  }}
                />
                {controlItem.type === 'password' && (
                  <button
                    type="button"
                    className="absolute top-1/2 right-3 -translate-y-1/2 flex items-center text-gray-500"
                    onClick={() => togglePassword(controlItem.name)}
                  >
                    {showPassword[controlItem.name] ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                )}
              </div>
              {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            </div>
          )
        }
        break
      // This is inside your form rendering logic, likely where you iterate over bussinessSignInFormControls
      // and render the appropriate component based on componentType.

      case 'select': // This is the relevant part for your Combobox
        element = (
          <Combobox
            className={cn('w-full', controlItem.className)} // Pass className for the button
            contentClassName={cn(
              'max-h-60 overflow-y-auto w-[var(--radix-popover-trigger-width)]',
              controlItem.contentClassName // Pass contentClassName for the dropdown
            )}
            options={
              controlItem.options?.map((option) => ({
                ...option,
                value: option[controlItem.optionValueKey || 'id'] || option.value, // Ensure 'value' exists
                label: option.label || option.id, // Ensure 'label' exists for searchKey
              })) ?? []
            }
            field={{
              value: value,
              onChange: (val) => {
                handleChange(controlItem.name, val, controlItem.validations)
                validateAndUpdateField(controlItem.name, val, controlItem.validations, formData)
              },
            }}
            searchKey={controlItem.searchKey || 'label'}
            placeholder={controlItem.placeholder || 'Select an option...'}
            icon={ChevronDown} //  pass the Chevron Down icon
          />
        )
        break
      case 'textarea':
        element = (
          <div className="w-full">
            <Textarea
              id={name}
              name={name}
              placeholder={controlItem.placeholder}
              value={value}
              onChange={(e) => handleChange(name, e.target.value, validations)}
              onBlur={() => validateAndUpdateField(name, value, validations)}
              className={`${controlItem.className || ''} ${error ? 'border-red-500' : ''}`}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
        )
        break
      case 'checkbox':
        element = (
          <div className="mt-3 flex space-x-2 items-start">
            <div className="flex items-center h-5 justify-center">
              <Checkbox
                id={name}
                name={name}
                checked={value || false}
                onCheckedChange={(checked) => handleChange(name, checked, validations)}
                className={error ? 'border-red-500' : ''}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-gray-600">
                {controlItem.label}{' '}
                {name === 'acceptTerms' ? (
                  <>
                    <span
                      onClick={handleTermsButtonClick}
                      className="text-heading-primary cursor-pointer hover:border-b hover:border-heading-primary"
                    >
                      Terms
                    </span>
                    {' and '}
                    <span
                      onClick={handlePrivacyPolicyButtonClick}
                      className="text-heading-primary cursor-pointer hover:border-b hover:border-heading-primary"
                    >
                      Privacy policy
                    </span>
                  </>
                ) : null}
              </span>
              {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            </div>
          </div>
        )
        break
      default:
        element = (
          <div className="w-full">
            <Input
              onInput={(e) => {
                // added so that user cant input spaces in start
                e.target.value = e.target.value.replace(/^\s+/, '')
              }}
              id={name}
              name={name}
              placeholder={controlItem.placeholder}
              type={controlItem.type}
              value={value}
              onChange={(e) => handleChange(name, e.target.value, validations)}
              onBlur={() => validateAndUpdateField(name, value, validations)}
              className={`${controlItem.className || ''} ${error ? 'border-red-500' : ''}`}
            />
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
        )
        break
    }

    return element
  }

  return (
    <div className="flex flex-col gap-4">
      {formControls.map((controlItem) => {
        if (controlItem.isGrouped) {
          const { groupControls } = controlItem
          const totalGrouped = groupControls.length

          const defaultBasisFraction = Math.max(3, Math.floor(12 / totalGrouped)) // basis-3/12 min
          const defaultBasisClass = `basis-${defaultBasisFraction}/12`

          return (
            <div
              key={controlItem.name}
              className="flex gap-4 justify-start flex-wrap md:flex-nowrap  items-center content-center"
            >
              {groupControls.map((groupedControl) => {
                const basisClass = groupedControl.basisClass || defaultBasisClass
                const wrapperClass = groupedControl.wrapperClassName || ''

                return (
                  <div key={groupedControl.name} className={`${basisClass} ${wrapperClass}`}>
                    <div className="flex flex-col w-full min-h-[100px]">
                      <Label htmlFor={groupedControl.name} className="text-gray-600 mb-3">
                        {groupedControl.label}
                        {groupedControl.validations?.some((v) => v.required) && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </Label>
                      {renderComponentByType(groupedControl)}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        }

        return (
          <div
            key={controlItem.name}
            className={`flex ${
              controlItem.componentType === 'checkbox' ? 'flex-row items-start gap-2' : 'flex-col'
            }`}
          >
            {controlItem.componentType !== 'checkbox' && (
              <Label htmlFor={controlItem.name} className="text-gray-600 mb-2">
                {controlItem.label}
                {controlItem.validations?.some((v) => v.required) && (
                  <span className="text-red-500 -ml-1">*</span>
                )}
              </Label>
            )}
            {renderComponentByType(controlItem)}
          </div>
        )
      })}
    </div>
  )
}

export default FormControls
